import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, FileText, ExternalLink, RefreshCw, BookOpen, Folder, Filter, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

// Role → audience filter mapping. Admins + IT see everything (no filter, server
// returns all docs regardless of audience tag). Managers and other roles see
// docs tagged for operator audience. The audience array is OR'd server-side
// via PostgreSQL's `&&` array-overlap operator.
function audienceForRoles(roles) {
  if (!Array.isArray(roles)) return ['operator'];
  const has = (r) => roles.some(x => (x || '').trim().toLowerCase() === r);
  if (has('admin') || has('it')) return null; // null = no filter, see all
  return ['operator', 'both'];
}

const FOLDER_LABELS = {
  projects:   { label: 'Projects',   color: '#0369a1' },
  references: { label: 'References', color: '#15803d' },
  feedback:   { label: 'Feedback',   color: '#b45309' },
  sessions:   { label: 'Sessions',   color: '#7c3aed' },
  handoffs:   { label: 'Handoffs',   color: '#be123c' },
  root:       { label: 'Root',       color: '#475569' },
};

const INPUT = { padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const PILL = (bg) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: bg + '15', color: bg,
  fontSize: 10.5, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase',
});

function formatRelativeTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

export default function KnowledgeBase() {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim()).filter(Boolean);
  const isAdmin = userRoles.some(r => (r || '').toLowerCase() === 'admin');
  const audienceFilter = useMemo(() => audienceForRoles(userRoles), [userRoles]);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // full doc row
  const [docLoading, setDocLoading] = useState(false);
  const [folder, setFolder] = useState(null);
  const [type, setType] = useState(null);
  const [facets, setFacets] = useState({ total: 0, last_synced: null, folders: [], types: [] });
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // Two-pill filter: Devs (dev-tagged content) vs Users (operator-tagged content).
  // Defaults derived from role: Admin/IT see both by default; Manager + others
  // see Users only by default. Both can toggle either pill at any time.
  const isAdminOrIT = audienceFilter === null;
  const [forDevs, setForDevs] = useState(isAdminOrIT);
  const [forUsers, setForUsers] = useState(true);
  const debounceRef = useRef(null);

  // Derived label — must come AFTER the useState hooks above (temporal dead zone)
  const audienceLabel = forDevs && forUsers
    ? 'Devs + Users'
    : forDevs ? 'Devs only' : forUsers ? 'Users only' : 'No filter selected';

  const effectiveAudience = !forDevs && !forUsers
    ? ['__none_selected__']  // sentinel: matches nothing → triggers empty state
    : (forDevs && forUsers ? null : (forDevs ? ['dev'] : ['operator']));

  const search = useCallback(async (q, fo, ty, aud) => {
    setLoading(true); setError('');
    try {
      const data = await neonQuery('kb_search', { q, folder: fo, type: ty, audience: aud, limit: 60 });
      setResults(Array.isArray(data) ? data : (data?.rows || data || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadFacets = useCallback(async (aud) => {
    try {
      const data = await neonQuery('kb_facets', { audience: aud });
      setFacets(data || { total: 0, last_synced: null, folders: [], types: [] });
    } catch { /* non-fatal */ }
  }, []);

  // Debounced search on query / filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query, folder, type, effectiveAudience), 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, folder, type, effectiveAudience, search]);

  // Initial facet load + reload when audience changes
  useEffect(() => { loadFacets(effectiveAudience); }, [effectiveAudience, loadFacets]);

  const openDoc = useCallback(async (path) => {
    setDocLoading(true); setError('');
    try {
      const data = await neonQuery('kb_get', { path });
      setSelected(data?.row || data);
    } catch (e) { setError(e.message); }
    finally { setDocLoading(false); }
  }, []);

  const triggerSync = useCallback(async () => {
    setSyncing(true); setSyncResult(null); setError('');
    try {
      const base = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions'
        : '/.netlify/functions';
      const res = await fetch(`${base}/kb-sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Sync failed: ${res.status}`);
      setSyncResult(data);
      // Refresh facets + current search after sync
      await loadFacets(effectiveAudience);
      await search(query, folder, type, effectiveAudience);
    } catch (e) { setError(e.message); }
    finally { setSyncing(false); }
  }, [effectiveAudience, loadFacets, query, folder, type, search]);

  const isEmpty = !loading && results.length === 0;
  const noFilterSelected = !forDevs && !forUsers;

  return (
    <div className="page-content" style={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header bar */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOpen size={20} style={{ color: 'var(--navy)' }} />
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>Knowledge Base</h1>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>
          <strong>{audienceLabel}</strong>
          {facets.last_synced && <> · synced {formatRelativeTime(facets.last_synced)}</>}
          {facets.total > 0 && <> · {facets.total} docs</>}
        </span>
        <div style={{ display: 'inline-flex', gap: 0, border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }} title="Filter by audience: Devs = code/architecture references; Users = step-by-step how-tos">
          <button
            onClick={() => setForDevs(v => !v)}
            style={{
              padding: '6px 12px',
              background: forDevs ? 'var(--navy)' : '#fff',
              color: forDevs ? '#fff' : 'var(--gray-600)',
              border: 'none',
              borderRight: '1px solid var(--gray-300)',
              fontSize: 11.5, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {forDevs ? '● ' : '○ '}Devs
          </button>
          <button
            onClick={() => setForUsers(v => !v)}
            style={{
              padding: '6px 12px',
              background: forUsers ? 'var(--orange)' : '#fff',
              color: forUsers ? '#fff' : 'var(--gray-600)',
              border: 'none',
              fontSize: 11.5, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {forUsers ? '● ' : '○ '}Users
          </button>
        </div>
        {isAdmin && (
          <button
            onClick={triggerSync}
            disabled={syncing}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: syncing ? 'default' : 'pointer', opacity: syncing ? 0.6 : 1 }}
          >
            <RefreshCw size={13} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
            {syncing ? 'Syncing...' : 'Sync now'}
          </button>
        )}
      </div>

      {syncResult && (
        <div style={{ padding: '6px 20px', background: '#f0fdf4', color: '#166534', fontSize: 12, borderBottom: '1px solid #bbf7d0' }}>
          Sync: {syncResult.upserted} upserted, {syncResult.pruned} pruned, {syncResult.errors || 0} errors. (HEAD {syncResult.head_sha?.slice(0,7)})
        </div>
      )}
      {error && (
        <div style={{ padding: '6px 20px', background: '#fef2f2', color: '#b91c1c', fontSize: 12, borderBottom: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      {/* Two-pane body */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Left: search + filters + results */}
        <div style={{ width: 380, borderRight: '1px solid var(--gray-200)', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--gray-200)' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--gray-400)' }} />
              <input
                type="text"
                placeholder="Search the KB..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ ...INPUT, paddingLeft: 32 }}
                autoFocus
              />
              {query && (
                <X size={14} onClick={() => setQuery('')} style={{ position: 'absolute', right: 10, top: 11, color: 'var(--gray-400)', cursor: 'pointer' }} />
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => setFolder(null)}
                style={{ padding: '3px 9px', background: !folder ? 'var(--navy)' : '#fff', color: !folder ? '#fff' : 'var(--gray-600)', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >All</button>
              {facets.folders.map(f => (
                <button
                  key={f.folder}
                  onClick={() => setFolder(f.folder === folder ? null : f.folder)}
                  style={{ padding: '3px 9px', background: folder === f.folder ? (FOLDER_LABELS[f.folder]?.color || 'var(--navy)') : '#fff', color: folder === f.folder ? '#fff' : 'var(--gray-700)', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                >{FOLDER_LABELS[f.folder]?.label || f.folder} <span style={{ opacity: 0.7 }}>({f.n})</span></button>
              ))}
            </div>
          </div>

          {/* Result list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading && (
              <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--gray-500)' }}>Searching...</div>
            )}
            {noFilterSelected && (
              <div style={{ padding: '24px 18px', fontSize: 12.5, color: 'var(--gray-600)', lineHeight: 1.55 }}>
                <strong style={{ color: 'var(--navy)' }}>No filter selected.</strong> Turn on <em>Devs</em> or <em>Users</em> at the top of the page to see docs.
              </div>
            )}
            {!loading && !noFilterSelected && results.length === 0 && (
              <div style={{ padding: 20, fontSize: 12, color: 'var(--gray-500)' }}>
                {query ? `No matches for "${query}".` : 'No documents found.'}
              </div>
            )}
            {results.map(row => (
              <button
                key={row.path}
                onClick={() => openDoc(row.path)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 14px', borderBottom: '1px solid var(--gray-100)',
                  background: selected?.path === row.path ? '#fff7ed' : 'transparent',
                  border: 'none', borderLeft: selected?.path === row.path ? '3px solid var(--orange)' : '3px solid transparent',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={PILL(FOLDER_LABELS[row.folder]?.color || '#475569')}>{FOLDER_LABELS[row.folder]?.label || row.folder}</span>
                  {row.type && <span style={{ fontSize: 10.5, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{row.type}</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 2, lineHeight: 1.3 }}>
                  {row.name || row.filename}
                </div>
                {row.description && (
                  <div style={{ fontSize: 11.5, color: 'var(--gray-600)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {row.description}
                  </div>
                )}
                {row.snippet && (
                  <div
                    style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4, fontStyle: 'italic', lineHeight: 1.4 }}
                    dangerouslySetInnerHTML={{ __html: '... ' + row.snippet }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right: rendered doc */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 36px', background: '#fff' }}>
          {docLoading && <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>}
          {!selected && !docLoading && (
            <div style={{ color: 'var(--gray-400)', fontSize: 13, marginTop: 80, textAlign: 'center' }}>
              <FileText size={36} style={{ color: 'var(--gray-300)', display: 'block', margin: '0 auto 12px' }} />
              Pick a document on the left to read.
            </div>
          )}
          {selected && (
            <article style={{ maxWidth: 880, margin: '0 auto', fontSize: 14, lineHeight: 1.7, color: '#1f2937' }}>
              <header style={{ borderBottom: '1px solid var(--gray-200)', paddingBottom: 14, marginBottom: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 11, color: 'var(--gray-500)', flexWrap: 'wrap' }}>
                  <span style={PILL(FOLDER_LABELS[selected.folder]?.color || '#475569')}>{FOLDER_LABELS[selected.folder]?.label || selected.folder}</span>
                  {selected.type && <span style={{ textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600 }}>{selected.type}</span>}
                  <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 10.5 }}>{selected.path}</span>
                </div>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--navy)', lineHeight: 1.25 }}>
                  {selected.name || selected.filename}
                </h1>
                {selected.description && (
                  <p style={{ margin: '8px 0 0 0', fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.5 }}>
                    {selected.description}
                  </p>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 10, fontSize: 11, color: 'var(--gray-500)' }}>
                  <span>Audience: {(selected.audience || ['dev']).join(' + ')}</span>
                  {selected.synced_at && <span>· Synced {formatRelativeTime(selected.synced_at)}</span>}
                  {selected.gh_url && (
                    <a href={selected.gh_url} target="_blank" rel="noreferrer" style={{ color: 'var(--orange)', display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 'auto' }}>
                      <ExternalLink size={11} /> Open in GitHub
                    </a>
                  )}
                </div>
              </header>
              <div className="kb-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{selected.body || ''}</ReactMarkdown>
              </div>
            </article>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .kb-markdown h1 { font-size: 19px; font-weight: 700; color: var(--navy); margin: 26px 0 10px; }
        .kb-markdown h2 { font-size: 16px; font-weight: 700; color: var(--navy); margin: 22px 0 8px; }
        .kb-markdown h3 { font-size: 14px; font-weight: 700; color: var(--navy); margin: 18px 0 6px; }
        .kb-markdown h4 { font-size: 13px; font-weight: 700; color: var(--gray-700); margin: 14px 0 4px; }
        .kb-markdown p  { margin: 0 0 12px; }
        .kb-markdown ul, .kb-markdown ol { margin: 0 0 12px 22px; padding: 0; }
        .kb-markdown li { margin: 0 0 4px; }
        .kb-markdown code { background: #f3f4f6; padding: 1px 5px; border-radius: 3px; font-size: 12px; font-family: ui-monospace,SFMono-Regular,Menlo,Consolas,monospace; }
        .kb-markdown pre { background: #1e293b; color: #e2e8f0; padding: 12px 14px; border-radius: 6px; overflow-x: auto; font-size: 12px; line-height: 1.55; margin: 0 0 14px; }
        .kb-markdown pre code { background: transparent; padding: 0; color: inherit; font-size: 12px; }
        .kb-markdown blockquote { border-left: 3px solid var(--orange); padding: 4px 14px; margin: 0 0 14px; color: var(--gray-700); background: #fff7ed; border-radius: 0 4px 4px 0; }
        .kb-markdown a { color: var(--orange); text-decoration: none; }
        .kb-markdown a:hover { text-decoration: underline; }
        .kb-markdown table { width: 100%; border-collapse: collapse; margin: 0 0 14px; font-size: 12.5px; }
        .kb-markdown th, .kb-markdown td { border: 1px solid var(--gray-200); padding: 6px 10px; text-align: left; }
        .kb-markdown th { background: #f9fafb; font-weight: 600; color: var(--navy); }
        .kb-markdown hr { border: 0; border-top: 1px solid var(--gray-200); margin: 22px 0; }
        .kb-markdown mark { background: #fef08a; color: inherit; padding: 0 2px; border-radius: 2px; }
      `}</style>
    </div>
  );
}
